import cx from "classnames";
import { Link, useLocation } from "react-router-dom";
import Screen from "modules/Core/sub-modules/ui-kit/components/Screen";
import Button from "modules/Core/sub-modules/ui-kit/components/Button";
import AuthenticationButton from "modules/auth/components/AuthenticationButton";
import styles from "./styles.module.scss";

export const LEGAL_LINKS = [
    { label: "Privacy", to: "/privacy" },
    { label: "Policies", to: "/policies" },
    { label: "Delete account", to: "/delete-account" },
    { label: "Contact", to: "/contact" },
];

const LegalHeader = ({ isAuthed }) => {
    const { pathname } = useLocation();
    return (
        <header className={styles.header}>
            <Link to="/homepage" className={styles.brand}>
                <span className={styles.brandMark}>
                    <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M12 22 L12 16 L20 12 L28 16 L28 22 L20 26 Z" stroke="white" strokeWidth="2" fill="none" />
                        <rect x="18" y="18" width="6" height="4" stroke="white" strokeWidth="1" fill="none" />
                        <path d="M20 10 L19 8 Q20 7 21 8 L20 10" stroke="white" strokeWidth="1" fill="white" opacity="0.8" />
                    </svg>
                </span>
                <span className={styles.brandName}>Transient Spaces</span>
            </Link>
            <nav className={styles.nav}>
                {LEGAL_LINKS.map((link) => (
                    <Link
                        key={link.to}
                        to={link.to}
                        className={cx(styles.navLink, { [styles.navLinkActive]: pathname === link.to })}
                    >
                        {link.label}
                    </Link>
                ))}
            </nav>
            <div className={styles.actions}>
                {isAuthed ? (
                    <Button to="/booths" className={styles.primaryAction}>Open app →</Button>
                ) : (
                    <AuthenticationButton className={styles.primaryAction} authMode="login">
                        Login
                    </AuthenticationButton>
                )}
            </div>
        </header>
    );
};

const LegalLayout = ({ isAuthed, eyebrow, title, meta = [], intro, sections, aside }) => (
    <Screen
        HeaderComponent={() => <LegalHeader isAuthed={isAuthed} />}
        className={styles.screen}
    >
        <section className={styles.hero}>
            <div className={styles.heroInner}>
                {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
                <h1 className={styles.title}>{title}</h1>
                {meta.length > 0 && (
                    <div className={styles.meta}>
                        {meta.map((item) => (
                            <span key={item.label} className={styles.metaItem}>
                                <span className={styles.metaLabel}>{item.label}</span>
                                {item.value}
                            </span>
                        ))}
                    </div>
                )}
                {intro && <div className={styles.intro}>{intro}</div>}
            </div>
        </section>

        <div className={styles.body}>
            <aside className={styles.toc}>
                <span className={styles.tocTitle}>On this page</span>
                <ol className={styles.tocList}>
                    {sections.map((section) => (
                        <li key={section.id}>
                            <a href={`#${section.id}`} className={styles.tocLink}>{section.title}</a>
                        </li>
                    ))}
                </ol>
                {aside && <div className={styles.tocAside}>{aside}</div>}
            </aside>

            <article className={styles.article}>
                {sections.map((section) => (
                    <section
                        key={section.id}
                        id={section.id}
                        className={cx(styles.section, { [styles.sectionHighlight]: section.highlight })}
                    >
                        <h2 className={styles.sectionTitle}>{section.title}</h2>
                        <div className={styles.prose}>{section.content}</div>
                    </section>
                ))}
            </article>
        </div>

        <footer className={styles.footer}>
            <span>© {new Date().getFullYear()} Transient Spaces</span>
            <nav className={styles.footerNav}>
                <Link to="/homepage">Home</Link>
                {LEGAL_LINKS.filter((link) => link.to !== "/delete-account").map((link) => (
                    <Link key={link.to} to={link.to}>{link.label}</Link>
                ))}
            </nav>
        </footer>
    </Screen>
);

export const Callout = ({ tone = "info", title, children }) => (
    <div className={cx(styles.callout, styles[`callout_${tone}`])}>
        {title && <strong className={styles.calloutTitle}>{title}</strong>}
        <div>{children}</div>
    </div>
);

export const ExternalLink = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children || href}</a>
);

export default LegalLayout;
